import type { Midi, MidiNote } from "@midi-fantom-utils/midi-core";
import { DRUM_CHANNEL } from "@midi-fantom-utils/midi-core";
import type { DetectedKey, KeyDetection } from "./types.js";
import { MIN_CONFIDENCE } from "./types.js";

const KS_MAJOR = [
  6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88,
];

const KS_MINOR = [
  6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17,
];

const CHROMATIC_KEYS = [
  "C", "C#", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B",
];

const MIN_NOTES = 6;

const pearson = (a: number[], b: number[]): number => {
  const n = a.length;
  let sumA = 0;
  let sumB = 0;
  for (let i = 0; i < n; i += 1) {
    sumA += a[i]!;
    sumB += b[i]!;
  }
  const meanA = sumA / n;
  const meanB = sumB / n;
  let num = 0;
  let denA = 0;
  let denB = 0;
  for (let i = 0; i < n; i += 1) {
    const da = a[i]! - meanA;
    const db = b[i]! - meanB;
    num += da * db;
    denA += da * da;
    denB += db * db;
  }
  const den = Math.sqrt(denA * denB);
  if (den === 0) return 0;
  return num / den;
};

const rotateLeft = (arr: number[], n: number): number[] => {
  const shift = ((n % arr.length) + arr.length) % arr.length;
  return arr.slice(shift).concat(arr.slice(0, shift));
};

const keyNameForTonic = (tonic: number): string =>
  CHROMATIC_KEYS[((tonic % 12) + 12) % 12]!;

export const collectNotesForKeyDetection = (midi: Midi): MidiNote[] => {
  const notes: MidiNote[] = [];
  for (const track of midi.tracks) {
    if (track.notes.length === 0) continue;
    // Формат-0 агрегат: все каналы в одном MTrk, и percussion-флаг трека
    // (по первому каналу / CC0=120) не отражает per-channel роль.
    // Определяем ударность по каналу каждой ноты; для single-channel трека
    // уважаем percussion-флаг как маркер «весь трек — ударные».
    const singleChannel = track.notes.every(
      (n) => (n.channel ?? track.channel) === track.channel
    );
    for (const note of track.notes) {
      const ch = note.channel ?? track.channel;
      if (ch === DRUM_CHANNEL) continue;
      if (singleChannel && track.instrument.percussion) continue;
      notes.push(note);
    }
  }
  return notes;
};

export const detectKey = (
  notes: ReadonlyArray<MidiNote>,
  options?: { minConfidence?: number }
): KeyDetection => {
  const minConfidence = options?.minConfidence ?? MIN_CONFIDENCE;

  if (notes.length < MIN_NOTES) {
    return { best: null, alternatives: [], noteCount: notes.length };
  }

  const profile = new Array<number>(12).fill(0);
  for (const note of notes) {
    const pc = ((note.midi % 12) + 12) % 12;
    const weight = Math.max(0, note.durationTicks) * Math.max(0, note.velocity);
    profile[pc]! += weight;
  }

  const profileSum = profile.reduce((a, b) => a + b, 0);
  if (profileSum === 0) {
    return { best: null, alternatives: [], noteCount: notes.length };
  }

  type Candidate = {
    key: string;
    scale: "major" | "minor";
    confidence: number;
    r: number;
  };

  const candidates: Candidate[] = [];

  for (let tonic = 0; tonic < 12; tonic += 1) {
    const shift = (12 - (tonic % 12)) % 12;
    const rotatedMajor = rotateLeft(KS_MAJOR, shift);
    const rotatedMinor = rotateLeft(KS_MINOR, shift);
    const rMajor = pearson(profile, rotatedMajor);
    const rMinor = pearson(profile, rotatedMinor);

    candidates.push({
      key: keyNameForTonic(tonic),
      scale: "major",
      r: rMajor,
      confidence: (rMajor + 1) / 2,
    });
    candidates.push({
      key: keyNameForTonic(tonic),
      scale: "minor",
      r: rMinor,
      confidence: (rMinor + 1) / 2,
    });
  }

  candidates.sort((a, b) => b.r - a.r);

  const best = candidates[0]!;
  if (best.confidence < minConfidence) {
    return {
      best: null,
      alternatives: candidates.slice(0, 3).map((c) => ({
        key: c.key,
        scale: c.scale,
        confidence: c.confidence,
      })),
      noteCount: notes.length,
    };
  }

  const alts: DetectedKey[] = [];
  const seen = new Set<string>();
  for (const c of candidates) {
    const sig = `${c.key}|${c.scale}`;
    if (seen.has(sig)) continue;
    seen.add(sig);
    alts.push({ key: c.key, scale: c.scale, confidence: c.confidence });
    if (alts.length >= 3) break;
  }

  return {
    best: {
      key: best.key,
      scale: best.scale,
      confidence: best.confidence,
    },
    alternatives: alts,
    noteCount: notes.length,
  };
};

export const isKeyDetectionEmpty = (d: KeyDetection): boolean =>
  d.noteCount === 0;